<template>
  <v-dialog v-model="model" max-width="1200px" persistent>
    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>{{ $t("Importer des bons (Excel)") }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close" :disabled="busy">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text>
        <v-alert color="secondary" outlined class="mb-4 mt-3">
          {{
            $t(
              "Sélectionnez une société, puis un fichier Excel conforme au modèle. Les véhicules sont vérifiés automatiquement. Les lignes invalides sont surlignées."
            )
          }}
          <div class="mt-2">
            <v-btn text small @click="downloadTemplate" :disabled="busy">
              <v-icon left>mdi-download</v-icon>{{ $t("Télécharger le modèle") }}
            </v-btn>
          </div>
        </v-alert>

        <!-- SOCIETE -->
        <v-row class="mt-3 mb-1">
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedCompanyPk"
              :items="companies"
              item-text="name"
              item-value="pk"
              outlined
              dense
              clearable
              :disabled="busy || lockCompany"
              :label="$t('Entreprise')"
              @change="onCompanyChange"
            />
            <div class="text-caption grey--text mt-1" v-if="lockCompany">
              {{ $t("Entreprise verrouillée selon les règles de création de bon.") }}
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="defaultProductId"
              :items="companyProducts"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              :disabled="busy || !selectedCompanyPk"
              :label="$t('Produit par défaut (optionnel)')"
              @change="validateAll"
            />
          </v-col>
        </v-row>

        <v-row class="mb-3">
          <v-col cols="12" md="6">
            <v-file-input
              v-model="file"
              :label="$t('Fichier Excel (.xlsx)')"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              prepend-icon="mdi-microsoft-excel"
              outlined
              dense
              show-size
              :disabled="busy"
              @change="onFileChange"
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-end">
            <v-alert v-if="!selectedCompanyPk" type="warning" dense outlined class="mb-0 w-100">
              {{ $t("Veuillez sélectionner une entreprise avant d'importer.") }}
            </v-alert>
          </v-col>
        </v-row>

        <v-alert v-if="errors.length" color="error" outlined class="mb-4">
          <div class="font-weight-bold mb-1">{{ $t("Erreurs de parsing") }}</div>
          <div v-for="(e, i) in errors" :key="i">- {{ e }}</div>
        </v-alert>

        <div v-if="rows.length">
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="subtitle-2 grey--text">
              {{ $t("Aperçu & édition") }} — {{ rows.length }} {{ $t("ligne(s) détectée(s)") }}
            </div>
            <div class="d-flex align-center">
              <v-chip class="mr-2" small>
                {{ validCount }} {{ $t("valide(s)") }}
              </v-chip>
              <v-chip v-if="invalidCount" color="error" text-color="white" small>
                {{ invalidCount }} {{ $t("en erreur") }}
              </v-chip>
            </div>
          </div>

          <v-data-table
            :headers="tableHeaders"
            :items="rows"
            :item-class="getRowClass"
            :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            dense
            class="elevation-1 bgTable"
          >
            <!-- Delete row -->
            <template v-slot:item._actions="{ item }">
              <v-btn icon small color="error" @click.stop="deleteRow(item)" :disabled="busy">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <!-- Errors / Warnings -->
            <template v-slot:item._errors="{ item }">
              <div v-if="item._errors && item._errors.length">
                <v-chip color="error" text-color="white" small class="mr-1">
                  {{ item._errors[0] }}
                  <span v-if="item._errors.length > 1">(+{{ item._errors.length - 1 }})</span>
                </v-chip>
              </div>
              <div v-if="item._warnings && item._warnings.length" class="mt-1">
                <v-chip color="warning" text-color="black" small class="mr-1">
                  {{ item._warnings[0] }}
                  <span v-if="item._warnings.length > 1">(+{{ item._warnings.length - 1 }})</span>
                </v-chip>
              </div>
            </template>

            <!-- Editable -->
            <template v-for="col in editableText" v-slot:[`item.${col}`]="{ item }">
              <v-edit-dialog
                :key="col + '_' + item._row"
                :return-value.sync="item[col]"
                @save="onCellSave(item, col)"
              >
                <span>{{ item[col] }}</span>
                <template v-slot:input>
                  <v-text-field v-model="item[col]" dense outlined hide-details />
                </template>
              </v-edit-dialog>
            </template>

            <!-- Vehicle status -->
            <template v-slot:item._vehicle_status="{ item }">
              <div class="text-caption">
                <span v-if="item._vehicle_loading">
                  <v-progress-circular indeterminate size="16" class="mr-2" />
                  {{ $t("Recherche véhicule…") }}
                </span>

                <span v-else-if="item.vehicle_id">
                  <v-chip small color="success" text-color="white">
                    OK
                  </v-chip>
                  <span class="ml-2 grey--text">
                    #{{ shortId(item.vehicle_id) }}
                  </span>
                  <span v-if="item._vehicle_count > 1" class="ml-2 grey--text">
                    ({{ item._vehicle_count }} {{ $t("résultats, 1er pris") }})
                  </span>
                </span>

                <span v-else class="red--text">
                  {{ $t("Véhicule introuvable") }}
                </span>
              </div>
            </template>

            <!-- Liters -->
            <template v-slot:item.purchase_liters="{ item }">
              <v-edit-dialog :return-value.sync="item.purchase_liters" @save="() => onLitersSave(item)">
                <span>{{ item.purchase_liters }}</span>
                <template v-slot:input>
                  <v-text-field v-model="item.purchase_liters" type="number" dense outlined hide-details />
                </template>
              </v-edit-dialog>
            </template>

            <!-- Amount -->
            <template v-slot:item.purchase_amount="{ item }">
              <v-edit-dialog :return-value.sync="item.purchase_amount" @save="() => onAmountSave(item)">
                <span>{{ item.purchase_amount }}</span>
                <template v-slot:input>
                  <v-text-field v-model="item.purchase_amount" type="number" dense outlined hide-details />
                </template>
              </v-edit-dialog>
            </template>

            <!-- Product -->
            <template v-slot:item.company_product="{ item }">
              <v-edit-dialog :return-value.sync="item.company_product" @save="() => onProductSave(item)">
                <span>{{ resolveProductName(item.company_product) || item.company_product || "-" }}</span>
                <template v-slot:input>
                  <v-select
                    :items="companyProducts"
                    item-text="name"
                    item-value="id"
                    v-model="item.company_product"
                    dense
                    outlined
                    hide-details
                    clearable
                  />
                </template>
              </v-edit-dialog>
            </template>

            <!-- Frequency -->
            <template v-slot:item.frequency="{ item }">
              <v-edit-dialog
                :return-value.sync="item.frequency"
                @save="() => { item.frequency = resolveFrequencyToCodeOrKeep(item.frequency); validateRow(item) }"
              >
                <span>{{ resolveFrequencyLabel(item.frequency) || item.frequency || "-" }}</span>
                <template v-slot:input>
                  <v-select
                    :items="frequencyItems"
                    item-text="text"
                    item-value="value"
                    v-model="item.frequency"
                    dense
                    outlined
                    hide-details
                    clearable
                  />
                </template>
              </v-edit-dialog>
            </template>

            <!-- allow_successive -->
            <template v-slot:item.allow_successive="{ item }">
              <v-switch v-model="item.allow_successive" inset @change="validateRow(item)" />
            </template>

            <!-- valid_until -->
            <template v-slot:item.valid_until="{ item }">
              <v-edit-dialog :return-value.sync="item.valid_until" @save="validateRow(item)">
                <span>{{ item.valid_until }}</span>
                <template v-slot:input>
                  <v-text-field v-model="item.valid_until" placeholder="YYYY-MM-DD" dense outlined hide-details />
                </template>
              </v-edit-dialog>
            </template>
          </v-data-table>

          <div class="text-caption mt-1 grey--text">
            {{ $t("Double-cliquez sur une cellule pour l'éditer. La date de fin est envoyée à 23:59 automatiquement.") }}
          </div>
        </div>

        <div v-if="busy" class="mt-4">
          <v-progress-linear :value="progress" height="8" />
          <div class="text-caption mt-1">
            {{ $t("Import en cours") }}… {{ done }}/{{ total }}
          </div>
        </div>

        <v-alert v-if="result.errors && result.errors.length" color="warning" outlined class="mt-4">
          <div class="font-weight-bold mb-1">{{ $t("Erreurs côté API") }}</div>
          <div v-for="(e, i) in result.errors" :key="'e' + i">- {{ e }}</div>
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="busy">{{ $t("commons.cancel") }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!canImport || invalidCount === rows.length || busy || !selectedCompanyPk"
          @click="importRows"
        >
          <v-icon left>mdi-upload</v-icon>{{ $t("Importer") }} ({{ validCount }}/{{ rows.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { CompanyVouchersService, CompanyService } from "@/services"
import CompanyVehiclesService from "@/services/CompanyVehiclesService"

const EXPECTED_HEADERS = {
  bl_number: ["N° BL", "BL", "bl_number"],
  vehicle: ["Véhicule", "Vehicule", "vehicle", "Matricule", "Véhicule (ID ou Matricule)"],
  company_product: ["Produit", "Type de carburant", "product", "company_product", "Produit (ID ou Nom)"],
  frequency: ["Fréquence", "Frequence", "frequency"],
  purchase_liters: ["Litres", "Litre", "purchase_liters"],
  purchase_amount: ["Montant Total", "Montant", "purchase_amount"],
  allow_successive: ["Autoriser superposition (Oui/Non)", "Superposition", "allow_successive"],
  valid_until: ["Date de fin", "Date fin", "valid_until", "Date de fin (YYYY-MM-DD)"],
  destination: ["Destination", "destination"]
}

export default {
  name: "ImportVoucherDialog",
  props: { value: { type: Boolean, default: false } },

  data () {
    return {
      model: this.value,
      file: null,
      rows: [],
      mapping: {},
      errors: [],

      companies: [],
      selectedCompanyPk: null,
      busy: false,

      companyProducts: [],
      defaultProductId: null,

      total: 0,
      done: 0,
      progress: 0,
      result: { errors: [] }
    }
  },

  watch: {
    value (v) { this.model = v },
    model (v) { this.$emit("input", v) }
  },

  computed: {
    isPrincipal () {
      return !!(this.$store.getters["app/getCompany"]?.is_principal)
    },
    isBusiness () {
      return (this.$store.getters["app/getCompany"]?.company_type || "").toLowerCase() === "business"
    },
    lockCompany () {
      return this.isPrincipal || (this.companies.length === 1 && !this.isBusiness)
    },

    tableHeaders () {
      return [
        { text: "", value: "_actions", width: 60, sortable: false },
        { text: this.$t("Erreurs"), value: "_errors", width: 260, sortable: false },
        { text: this.$t("N° BL"), value: "bl_number" },
        { text: this.$t("Véhicule"), value: "vehicle" },
        { text: this.$t("Statut véhicule"), value: "_vehicle_status", width: 220, sortable: false },
        { text: this.$t("Produit"), value: "company_product" },
        { text: this.$t("Fréquence"), value: "frequency" },
        { text: this.$t("Litres"), value: "purchase_liters" },
        { text: this.$t("Montant"), value: "purchase_amount" },
        { text: this.$t("Superposition"), value: "allow_successive", align: "center", width: 120 },
        { text: this.$t("Date de fin"), value: "valid_until" },
        { text: this.$t("Destination"), value: "destination" }
      ]
    },

    editableText () {
      return ["bl_number", "vehicle", "destination"]
    },

    canImport () {
      return this.rows.length > 0 && this.validCount > 0
    },
    validCount () {
      return this.rows.filter(r => !r._errors || r._errors.length === 0).length
    },
    invalidCount () {
      return this.rows.length - this.validCount
    },

    frequencyItems () {
      return [
        { text: this.$t("Non renouvelable"), value: "Single" },
        { text: this.$t("Journalier"), value: "Daily" },
        { text: this.$t("Hebdomadaire"), value: "Weekly" },
        { text: this.$t("Mensuel"), value: "Monthly" },
        { text: this.$t("Trimestriel"), value: "Quarterly" },
        { text: this.$t("Semestriel"), value: "SemiAnnual" },
        { text: this.$t("Annuel"), value: "Annual" }
      ]
    }
  },

  async created () {
    const companies = await CompanyService.getAll().catch(() => [])
    this.companies = companies || []

    if (this.lockCompany && this.companies.length) {
      this.selectedCompanyPk = this.companies[0].pk
      await this.onCompanyChange()
    }
  },

  methods: {
    shortId (id) {
      const s = String(id || "")
      return s.length > 8 ? s.slice(0, 8) : s
    },

    normalize (s) {
      return (s || "").toString().trim().toLowerCase()
    },
    parseNumberOrEmpty (v) {
      const n = Number(String(v).replace(/\s+/g, "").replace(",", "."))
      return Number.isFinite(n) ? n : ""
    },
    boolish (v) {
      const s = this.normalize(v)
      return ["1", "true", "oui", "yes", "y", "vrai"].includes(s)
    },

    resolveProductName (id) {
      if (id === "" || id === null || id === undefined) return null
      const p = (this.companyProducts || []).find(x => x.id === Number(id))
      return p ? p.name : null
    },

    resolveProductToId (val) {
      if (val === null || val === undefined || val === "") return null
      if (!isNaN(Number(val))) return Number(val)
      let name = this.normalize(val)
      if (name === "essence") name = "super"
      const p = (this.companyProducts || []).find(x => this.normalize(x.name) === name)
      return p ? p.id : null
    },

    // adapte si ton champ est différent
    getProductUnitPrice (productId) {
      const p = (this.companyProducts || []).find(x => x.id === Number(productId))
      if (!p) return null
      const price = p.unit_price ?? p.price ?? p.amount ?? null
      const n = Number(price)
      return Number.isFinite(n) ? n : null
    },

    autoFixProductFromVehicle (row) {
      const vProd = row._vehicle_product_name_norm // "super" | "gasoil" | null
      if (!vProd) return

      const currentId = this.resolveProductToId(row.company_product)
      const currentNameNorm = currentId
        ? this.normalize(this.resolveProductName(currentId))
        : this.normalize(row.company_product)

      if (vProd === "gasoil" && currentNameNorm !== "gasoil") {
        const gasoilId = this.resolveProductToId("gasoil")
        if (gasoilId) {
          row.company_product = gasoilId
          row._warnings.push(this.$t("Produit corrigé automatiquement: Gasoil"))
        }
      }

      if (vProd === "super" && currentNameNorm !== "super") {
        const superId = this.resolveProductToId("super")
        if (superId) {
          row.company_product = superId
          row._warnings.push(this.$t("Produit corrigé automatiquement: Super"))
        }
      }
    },

    autoComputeAmount (row) {
      const liters = Number(row.purchase_liters || 0)
      if (!(liters > 0)) return
      const productId = this.resolveProductToId(row.company_product)
      if (!productId) return
      const unit = this.getProductUnitPrice(productId)
      if (!(unit > 0)) return
      row.purchase_amount = Math.round(liters * unit)
    },

    resolveFrequencyToCodeOrKeep (val) {
      if (val === null || val === undefined || val === "") return ""
      const allowed = new Set(this.frequencyItems.map(x => x.value))
      if (allowed.has(val)) return val

      const s = this.normalize(val)
      const map = {
        "non renouvelable": "Single",
        "non-renouvelable": "Single",
        "single": "Single",
        "journalier": "Daily",
        "quotidien": "Daily",
        "daily": "Daily",
        "hebdomadaire": "Weekly",
        "weekly": "Weekly",
        "mensuel": "Monthly",
        "monthly": "Monthly",
        "trimestriel": "Quarterly",
        "quarterly": "Quarterly",
        "semestriel": "SemiAnnual",
        "semiannual": "SemiAnnual",
        "annuel": "Annual",
        "annual": "Annual"
      }
      return map[s] || val
    },
    resolveFrequencyLabel (code) {
      const it = this.frequencyItems.find(x => x.value === code)
      return it ? it.text : null
    },

    toEndOfDayIso (dateStr) {
      const d = String(dateStr || "").trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null
      return `${d}T23:59:00Z`
    },

    deleteRow (item) {
      this.rows = this.rows.filter(r => r._row !== item._row)
    },

    close () {
      if (this.busy) return
      this.reset()
      this.model = false
    },
    reset () {
      this.file = null
      this.rows = []
      this.mapping = {}
      this.errors = []
      this.busy = false
      this.total = 0
      this.done = 0
      this.progress = 0
      this.result = { errors: [] }
      this.defaultProductId = null
      if (!this.lockCompany) this.selectedCompanyPk = null
    },

    async onCompanyChange () {
      const company = this.companies.find(c => c.pk === this.selectedCompanyPk)
      if (!company) {
        this.companyProducts = []
        this.validateAll()
        return
      }
      this.companyProducts = (company.products || []).map(p => ({ ...p }))
      this.validateAll()
    },

    buildMapping (headers) {
      const map = {}
      const norm = s => (s || "").toString().trim().toLowerCase()

      headers.forEach(h => {
        const hNorm = norm(h)
        for (const [field, aliases] of Object.entries(EXPECTED_HEADERS)) {
          if (aliases.map(a => norm(a)).includes(hNorm)) {
            map[field] = h
          }
        }
      })

      if (!map.vehicle) throw new Error(this.$t("Colonnes manquantes") + " : Véhicule")
      if (!map.valid_until) throw new Error(this.$t("Colonnes manquantes") + " : Date de fin")
      if (!map.company_product && !this.defaultProductId) {
        throw new Error(this.$t("Colonnes manquantes") + " : Produit (ou choisissez un produit par défaut)")
      }
      return map
    },

    async onFileChange () {
      this.errors = []
      this.rows = []
      if (!this.file) return

      if (!this.selectedCompanyPk) {
        this.errors = [this.$t("Veuillez sélectionner une entreprise avant d'importer.")]
        return
      }

      try {
        const buf = await this.file.arrayBuffer()
        const wb = XLSX.read(buf, { type: "array" })
        const sheetName = wb.SheetNames[0]
        const ws = wb.Sheets[sheetName]
        if (!ws) throw new Error(this.$t("Aucune feuille trouvée"))

        const json = XLSX.utils.sheet_to_json(ws, { defval: "" })
        if (!json.length) {
          this.errors = [this.$t("Aucune donnée détectée")]
          return
        }

        const headers = Object.keys(json[0] || {})
        this.mapping = this.buildMapping(headers)

        this.rows = json.map((row, i) => {
          const obj = { _row: i + 2, _errors: [], _warnings: [], _vehicle_loading: false }

          Object.entries(this.mapping).forEach(([field, key]) => { obj[field] = row[key] ?? "" })

          obj.purchase_liters = this.parseNumberOrEmpty(obj.purchase_liters)
          obj.purchase_amount = this.parseNumberOrEmpty(obj.purchase_amount)
          obj.allow_successive = this.boolish(obj.allow_successive)
          obj.frequency = this.resolveFrequencyToCodeOrKeep(obj.frequency)

          let prodId = this.resolveProductToId(obj.company_product)
          if (!prodId && this.defaultProductId) prodId = this.defaultProductId
          obj.company_product = prodId ?? (obj.company_product || "")

          // ✅ UUID string
          obj.vehicle_id = null
          obj._vehicle_count = 0
          obj._vehicle_product_name_norm = null

          if (String(obj.vehicle || "").trim() || String(obj.bl_number || "").trim()) return obj
          return null
        }).filter(Boolean)

        await this.resolveVehiclesForAllRows()
        this.validateAll()
      } catch (e) {
        this.errors.push(e?.message || String(e))
      }
    },

    async resolveOneVehicleForRow (row) {
      const q = String(row.vehicle || "").trim()
      row._vehicle_loading = true
      row.vehicle_id = null
      row._vehicle_count = 0
      row._vehicle_product_name_norm = null

      try {
        if (!q) {
          row._errors.push(this.$t("Véhicule requis (ID ou matricule)"))
          return
        }

        const resp = await CompanyVehiclesService.resolve(this, q)
        const vehicle = resp?.vehicle || null
        const count = Number(resp?.count || 0)

        if (!vehicle || !vehicle.id) {
          row._errors.push(this.$t("Le véhicule n'existe pas"))
          return
        }

        // ✅ garder UUID tel quel
        row.vehicle_id = String(vehicle.id)
        row._vehicle_count = Number.isFinite(count) ? count : 1

        const vehProdName = vehicle?.product?.name ? this.normalize(vehicle.product.name) : null
        row._vehicle_product_name_norm = vehProdName

        if (row._vehicle_count > 1) {
          row._warnings.push(this.$t("Plusieurs véhicules trouvés: le 1er a été pris"))
        }
      } catch (e) {
        row._errors.push(this.$t("Le véhicule n'existe pas"))
      } finally {
        row._vehicle_loading = false
      }
    },

    async resolveVehiclesForAllRows () {
      const concurrency = 5
      const queue = [...this.rows]

      const workers = new Array(concurrency).fill(0).map(async () => {
        while (queue.length) {
          const row = queue.shift()
          row._errors = []
          row._warnings = []

          await this.resolveOneVehicleForRow(row)
          this.autoFixProductFromVehicle(row)
          this.autoComputeAmount(row)
          this.validateRow(row)
        }
      })

      await Promise.all(workers)
    },

    async onCellSave (row, col) {
      if (col === "vehicle") {
        row._errors = []
        row._warnings = []
        await this.resolveOneVehicleForRow(row)
        this.autoFixProductFromVehicle(row)
        this.autoComputeAmount(row)
        this.validateRow(row)
        return
      }
      this.validateRow(row)
    },

    onLitersSave (row) {
      row.purchase_liters = this.parseNumberOrEmpty(row.purchase_liters)
      this.autoComputeAmount(row)
      this.validateRow(row)
    },

    onAmountSave (row) {
      row.purchase_amount = this.parseNumberOrEmpty(row.purchase_amount)
      this.validateRow(row)
    },

    onProductSave (row) {
      const prodId = this.resolveProductToId(row.company_product)
      row.company_product = prodId ?? row.company_product

      // ✅ si véhicule impose Super/Gasoil, on remet automatiquement
      this.autoFixProductFromVehicle(row)

      this.autoComputeAmount(row)
      this.validateRow(row)
    },

    validateAll () {
      this.rows.forEach(r => this.validateRow(r))
    },

    validateRow (r) {
      const errs = []
      const warns = r._warnings || []

      if (!this.selectedCompanyPk) errs.push(this.$t("Entreprise obligatoire (sélection en haut)"))

      // ✅ UUID ok => juste vérifier que c'est présent
      if (!r.vehicle_id) errs.push(this.$t("Le véhicule n'existe pas"))

      const prodId = this.resolveProductToId(r.company_product)
      if (!prodId || isNaN(Number(prodId))) errs.push(this.$t("Produit requis (ID ou nom)"))
      else r.company_product = prodId

      const allowed = new Set(this.frequencyItems.map(x => x.value))
      const freq = this.resolveFrequencyToCodeOrKeep(r.frequency)
      r.frequency = freq
      if (!allowed.has(freq)) errs.push(this.$t("Fréquence invalide"))

      if (!String(r.valid_until || "").trim()) errs.push(this.$t("Date de fin requise (YYYY-MM-DD)"))
      else if (!/^\d{4}-\d{2}-\d{2}$/.test(String(r.valid_until).trim())) errs.push(this.$t("Date de fin invalide (format YYYY-MM-DD)"))

      const liters = Number(r.purchase_liters || 0)
      const amount = Number(r.purchase_amount || 0)
      if (liters <= 0 && amount <= 0) errs.push(this.$t("Renseignez au moins Litres ou Montant"))

      // warning si prix détecté mais montant vide
      if (liters > 0 && (!amount || amount <= 0)) {
        const unit = this.getProductUnitPrice(r.company_product)
        if (unit > 0) warns.push(this.$t("Montant recalculable automatiquement (prix produit détecté)"))
      }

      r._warnings = warns
      r._errors = errs
      return errs.length === 0
    },

    async importRows () {
      if (!this.canImport || !this.selectedCompanyPk) return

      this.busy = true
      this.result = { errors: [] }
      this.total = this.rows.length
      this.done = 0
      this.progress = 0

      const toImport = this.rows.filter(r => !r._errors || r._errors.length === 0)

      for (const r of toImport) {
        const validUntilIso = this.toEndOfDayIso(r.valid_until)

        const payload = {
          bl_number: r.bl_number || null,
          recipient_company: Number(this.selectedCompanyPk),

          // ✅ UUID string
          vehicle: String(r.vehicle_id),

          purchase_liters: r.purchase_liters === "" || r.purchase_liters === null ? null : Number(r.purchase_liters),
          purchase_amount: r.purchase_amount === "" || r.purchase_amount === null ? null : Number(r.purchase_amount),

          company_product: r.company_product ? Number(r.company_product) : null,

          // plus de colonne station => tout le réseau
          company_shop: null,

          frequency: r.frequency || "Single",
          allow_successive: !!r.allow_successive,
          valid_until: validUntilIso,
          destination: r.destination || null
        }

        try {
          await CompanyVouchersService.create(this, payload)
        } catch (e) {
          const msg = this.buildApiError(e)
          const label = r.bl_number || r.vehicle || "ligne"
          this.result.errors.push(`${label} — ${msg}`)
        } finally {
          this.done += 1
          this.progress = Math.round((this.done / this.total) * 100)
        }
      }

      const hasErrors = this.result.errors.length > 0
      if (!hasErrors) {
        this.$emit("imported", { total: this.total, failed: 0 })
        this.$store.commit("app/message", { msg: this.$t("Import terminé avec succès"), color: "success" })
        this.close()
      } else {
        this.$store.commit("app/message", { msg: this.$t("Import terminé avec des erreurs"), color: "warning" })
      }

      this.busy = false
    },

    buildApiError (err) {
      if (err?.response?.data) {
        const data = err.response.data
        try {
          if (typeof data === "string") return data
          const parts = []
          Object.keys(data).forEach(k => {
            const v = data[k]
            if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`)
            else if (typeof v === "object" && v !== null) {
              Object.keys(v).forEach(sk => parts.push(`${k}.${sk}: ${v[sk]}`))
            } else parts.push(`${k}: ${v}`)
          })
          return parts.join(" | ")
        } catch {
          return this.$t("Erreur inconnue")
        }
      }
      return err?.message || this.$t("Erreur inconnue")
    },

    downloadTemplate () {
      const headersFR = [
        "N° BL",
        "Véhicule (ID ou Matricule)",
        "Produit (ID ou Nom)",
        "Fréquence",
        "Litres",
        "Montant Total",
        "Autoriser superposition (Oui/Non)",
        "Date de fin (YYYY-MM-DD)",
        "Destination"
      ]
      const sample = [
        "BL-0001",
        "3678 WWCI 01",
        "Gasoil",
        "Non renouvelable",
        "50",
        "",
        "Non",
        "2026-01-31",
        "Abidjan"
      ]

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headersFR, sample])
      XLSX.utils.book_append_sheet(wb, ws, "Template")
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      saveAs(
        new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        "modele_import_bons.xlsx"
      )
    },

    getRowClass (item) {
      return item._errors && item._errors.length ? "row-error" : ""
    }
  }
}
</script>

<style scoped>
.row-error td { background-color: #ffebee !important; }
</style>
